/**
 * Usage Management Service
 * 
 * Comprehensive service for handling usage tracking, credit management,
 * and subscription-based limits with owner exclusions.
 * 
 * Features:
 * - Free usage priority (5 searches/day, 1 AI analysis/week)
 * - Credit-based overages
 * - Owner unlimited access
 * - Automatic limit resets
 * - Complete audit trail
 */

import { supabase } from '@/integrations/supabase/client';
import { debugLog, debugError, debugWarn } from '@/lib/debugUtils';
import type {
  UsageSummary,
  UsageResult,
  UsageCheckParams,
  UsageCheckResult,
  CreditPackage,
  CreditTransaction,
  UsageServiceConfig,
  UsageServiceError,
  UsageErrorType,
  UsageType,
  CreditType,
  DatabaseFunctionResult
} from '../types/usage';

/**
 * Default usage service configuration
 */
const DEFAULT_USAGE_CONFIG: UsageServiceConfig = {
  enableUsageTracking: true,
  enableOwnerBypass: true,
  autoResetLimits: true,
  debugLogging: true
};

/**
 * Enhanced usage service with comprehensive tracking and owner exclusions
 * Follows the same patterns as WorkspaceService for consistency
 */
export class UsageService {
  private config: UsageServiceConfig;

  constructor(config: UsageServiceConfig = DEFAULT_USAGE_CONFIG) {
    this.config = config;
  }

  /**
   * Create enhanced error object with context
   */
  private createError(
    type: UsageErrorType,
    message: string,
    details?: Record<string, any>
  ): UsageServiceError {
    const error = new UsageServiceError(type, message, details);
    
    if (this.config.debugLogging) {
      debugError('UsageService Error', {
        type,
        message,
        details,
        stack: error.stack
      });
    }
    
    return error;
  }

  /**
   * Debug logging helper
   */
  private log(message: string, data?: any): void {
    if (this.config.debugLogging) {
      debugLog(`UsageService: ${message}`, data);
    }
  }

  /**
   * Get comprehensive usage summary for a user
   */
  async getUsageSummary(userId: string): Promise<UsageSummary> {
    this.log('Getting usage summary', { userId });

    try {
      const { data, error } = await supabase.rpc('get_usage_summary', {
        p_user_id: userId
      });

      if (error) {
        throw this.createError(
          'DATABASE_ERROR',
          `Failed to get usage summary: ${error.message}`,
          { userId, originalError: error }
        );
      }

      if (!data || data.length === 0) {
        // Return default summary for new users
        return {
          is_owner: false,
          daily_searches_used: 0,
          daily_searches_remaining: 5,
          weekly_ai_analyses_used: 0,
          weekly_ai_analyses_remaining: 1,
          search_credits: 0,
          ai_analysis_credits: 0,
          total_search_credits_used: 0,
          total_ai_analysis_credits_used: 0
        };
      }

      this.log('Usage summary retrieved', { userId, summary: data[0] });
      return data[0];

    } catch (error) {
      if (error instanceof UsageServiceError) {
        throw error;
      }
      
      throw this.createError(
        'DATABASE_ERROR',
        `Unexpected error getting usage summary: ${error instanceof Error ? error.message : 'Unknown error'}`,
        { userId, originalError: error }
      );
    }
  }

  /**
   * Check and consume usage for an operation
   * Prioritizes free usage, then falls back to credits
   * Owners get unlimited access
   */
  async checkAndConsumeUsage(params: UsageCheckParams): Promise<UsageCheckResult> {
    const { userId, usageType, workspaceId, resourceId, metadata } = params;
    
    this.log('Checking and consuming usage', params);

    try {
      const { data, error } = await supabase.rpc('check_and_consume_usage', {
        p_user_id: userId,
        p_usage_type: usageType,
        p_workspace_id: workspaceId || null,
        p_resource_id: resourceId || null
      });

      if (error) {
        throw this.createError(
          'DATABASE_ERROR',
          `Failed to check usage: ${error.message}`,
          { ...params, originalError: error }
        );
      }

      if (!data || data.length === 0) {
        throw this.createError(
          'DATABASE_ERROR',
          'No usage result returned from database',
          params
        );
      }

      const result = data[0];
      this.log('Usage check result', { ...params, result });

      // Get updated usage summary for remaining balances
      let remainingFreeUsage;
      let remainingCredits;

      try {
        const summary = await this.getUsageSummary(userId);
        remainingFreeUsage = {
          daily_searches: summary.daily_searches_remaining,
          weekly_ai_analyses: summary.weekly_ai_analyses_remaining
        };
        remainingCredits = {
          search_credits: summary.search_credits,
          ai_analysis_credits: summary.ai_analysis_credits
        };
      } catch (summaryError) {
        this.log('Warning: Could not get updated summary after usage check', summaryError);
      }

      return {
        success: result.success,
        usage_type: result.usage_type,
        message: result.message,
        remainingFreeUsage,
        remainingCredits
      };

    } catch (error) {
      if (error instanceof UsageServiceError) {
        throw error;
      }
      
      throw this.createError(
        'DATABASE_ERROR',
        `Unexpected error checking usage: ${error instanceof Error ? error.message : 'Unknown error'}`,
        { ...params, originalError: error }
      );
    }
  }

  /**
   * Get available credit packages
   */
  async getCreditPackages(): Promise<CreditPackage[]> {
    this.log('Getting credit packages');

    try {
      const { data, error } = await supabase
        .from('credit_packages')
        .select('*')
        .eq('is_active', true)
        .order('price', { ascending: true });

      if (error) {
        throw this.createError(
          'DATABASE_ERROR',
          `Failed to get credit packages: ${error.message}`,
          { originalError: error }
        );
      }

      this.log('Credit packages retrieved', { count: data?.length || 0 });
      return data || [];

    } catch (error) {
      if (error instanceof UsageServiceError) {
        throw error;
      }
      
      throw this.createError(
        'DATABASE_ERROR',
        `Unexpected error getting credit packages: ${error instanceof Error ? error.message : 'Unknown error'}`,
        { originalError: error }
      );
    }
  }

  /**
   * Get user's credit transaction history
   */
  async getCreditTransactionHistory(
    userId: string,
    limit: number = 50
  ): Promise<CreditTransaction[]> {
    this.log('Getting credit transaction history', { userId, limit });

    try {
      const { data, error } = await supabase
        .from('credit_transactions')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) {
        throw this.createError(
          'DATABASE_ERROR',
          `Failed to get transaction history: ${error.message}`,
          { userId, limit, originalError: error }
        );
      }

      this.log('Transaction history retrieved', { userId, count: data?.length || 0 });
      return data || [];

    } catch (error) {
      if (error instanceof UsageServiceError) {
        throw error;
      }
      
      throw this.createError(
        'DATABASE_ERROR',
        `Unexpected error getting transaction history: ${error instanceof Error ? error.message : 'Unknown error'}`,
        { userId, limit, originalError: error }
      );
    }
  }

  /**
   * Add credits to user account (for purchases)
   */
  async addCredits(
    userId: string,
    creditType: CreditType,
    amount: number,
    packageId?: string,
    stripePaymentIntentId?: string
  ): Promise<void> {
    this.log('Adding credits', { userId, creditType, amount, packageId });

    try {
      const { error } = await supabase.rpc('add_credits', {
        p_user_id: userId,
        p_credit_type: creditType,
        p_amount: amount,
        p_package_id: packageId || null,
        p_stripe_payment_intent_id: stripePaymentIntentId || null
      });

      if (error) {
        throw this.createError(
          'DATABASE_ERROR',
          `Failed to add credits: ${error.message}`,
          { userId, creditType, amount, packageId, originalError: error }
        );
      }

      this.log('Credits added successfully', { userId, creditType, amount });

    } catch (error) {
      if (error instanceof UsageServiceError) {
        throw error;
      }
      
      throw this.createError(
        'DATABASE_ERROR',
        `Unexpected error adding credits: ${error instanceof Error ? error.message : 'Unknown error'}`,
        { userId, creditType, amount, packageId, originalError: error }
      );
    }
  }

  /**
   * Reset free usage limits (called automatically by database functions)
   */
  async resetFreeUsageLimits(): Promise<void> {
    this.log('Manually resetting free usage limits');

    try {
      const { error } = await supabase.rpc('reset_free_usage_limits');

      if (error) {
        throw this.createError(
          'DATABASE_ERROR',
          `Failed to reset free usage limits: ${error.message}`,
          { originalError: error }
        );
      }

      this.log('Free usage limits reset successfully');

    } catch (error) {
      if (error instanceof UsageServiceError) {
        throw error;
      }
      
      throw this.createError(
        'DATABASE_ERROR',
        `Unexpected error resetting limits: ${error instanceof Error ? error.message : 'Unknown error'}`,
        { originalError: error }
      );
    }
  }

  /**
   * Check if user can perform operation without consuming usage
   * Useful for UI state updates
   */
  async canPerformOperation(
    userId: string,
    usageType: UsageType
  ): Promise<{ canPerform: boolean; reason?: string; usageType?: string }> {
    this.log('Checking if operation can be performed', { userId, usageType });

    try {
      const summary = await this.getUsageSummary(userId);

      // Owners can always perform operations
      if (summary.is_owner) {
        return { canPerform: true, usageType: 'owner_unlimited' };
      }

      // Check free usage availability
      if (usageType === 'search' && summary.daily_searches_remaining > 0) {
        return { canPerform: true, usageType: 'free' };
      }

      if (usageType === 'ai_analysis' && summary.weekly_ai_analyses_remaining > 0) {
        return { canPerform: true, usageType: 'free' };
      }

      // Check credit availability
      if (usageType === 'search' && summary.search_credits > 0) {
        return { canPerform: true, usageType: 'credit' };
      }

      if (usageType === 'ai_analysis' && summary.ai_analysis_credits > 0) {
        return { canPerform: true, usageType: 'credit' };
      }

      // Cannot perform operation
      return {
        canPerform: false,
        reason: `Insufficient credits. Free ${usageType} limit reached.`
      };

    } catch (error) {
      this.log('Error checking operation capability', error);
      return {
        canPerform: false,
        reason: 'Unable to check usage limits'
      };
    }
  }

  /**
   * Get usage statistics for admin/analytics
   */
  async getUsageStatistics(
    startDate?: string,
    endDate?: string
  ): Promise<{
    totalOperations: number;
    freeOperations: number;
    creditOperations: number;
    ownerOperations: number;
    searchOperations: number;
    aiAnalysisOperations: number;
  }> {
    this.log('Getting usage statistics', { startDate, endDate });

    try {
      
      let query = supabase
        .from('usage_events')
        .select('event_type, usage_type');

      if (startDate) {
        query = query.gte('created_at', startDate);
      }
      if (endDate) {
        query = query.lte('created_at', endDate);
      }

      const { data, error } = await query;

      if (error) {
        throw this.createError(
          'DATABASE_ERROR',
          `Failed to get usage statistics: ${error.message}`,
          { startDate, endDate, originalError: error }
        );
      }

      const stats = {
        totalOperations: data?.length || 0,
        freeOperations: data?.filter(e => e.usage_type === 'free').length || 0,
        creditOperations: data?.filter(e => e.usage_type === 'credit').length || 0,
        ownerOperations: data?.filter(e => e.usage_type === 'owner_unlimited').length || 0,
        searchOperations: data?.filter(e => e.event_type === 'search').length || 0,
        aiAnalysisOperations: data?.filter(e => e.event_type === 'ai_analysis').length || 0
      };

      this.log('Usage statistics retrieved', stats);
      return stats;

    } catch (error) {
      if (error instanceof UsageServiceError) {
        throw error;
      }
      
      throw this.createError(
        'DATABASE_ERROR',
        `Unexpected error getting usage statistics: ${error instanceof Error ? error.message : 'Unknown error'}`,
        { startDate, endDate, originalError: error }
      );
    }
  }
}

/**
 * Default usage service instance
 */
export const usageService = new UsageService();

/**
 * Convenience functions for common operations
 */
export const UsageServiceHelpers = {
  /**
   * Check if error is a usage-related error
   */
  isUsageError(error: any): error is UsageServiceError {
    return error instanceof UsageServiceError;
  },

  /**
   * Check if operation failed due to insufficient credits
   */
  isInsufficientCreditsError(error: any): boolean {
    return this.isUsageError(error) && error.type === 'INSUFFICIENT_CREDITS';
  },

  /**
   * Check if operation failed due to free limit exceeded
   */
  isFreeLimitExceededError(error: any): boolean {
    return this.isUsageError(error) && error.type === 'FREE_LIMIT_EXCEEDED';
  },

  /**
   * Format usage type for display
   */
  formatUsageType(usageType: string): string {
    switch (usageType) {
      case 'free': return 'Free Usage';
      case 'credit': return 'Paid Credits';
      case 'owner_unlimited': return 'Owner Access';
      default: return 'Unknown';
    }
  },

  /**
   * Format credit type for display
   */
  formatCreditType(creditType: CreditType): string {
    switch (creditType) {
      case 'search': return 'Search Credits';
      case 'ai_analysis': return 'AI Analysis Credits';
      default: return 'Credits';
    }
  }
};